import { useEffect, useState } from "react";
import { FolderTree, Loader2, Pencil, Plus, Save, Trash2 } from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory,
  type CategoryRow,
} from "@/hooks/useIndicators";

/**
 * /categories — Director+ surface. Inline create at the top, list of
 * categories with edit/delete actions. Categories are the shared
 * taxonomy used by Indicators, KPI definitions and (in future) other
 * grouping surfaces. The code (C001, …) is auto-assigned server-side.
 *
 * Deleting a category removes the M2M link from every indicator that
 * had it — the confirm copy says so.
 */
export default function Categories() {
  const list    = useCategories();
  const create  = useCreateCategory();
  const update  = useUpdateCategory();
  const del     = useDeleteCategory();
  const confirm = useConfirm();

  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<CategoryRow | null>(null);

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    create.mutate(name, { onSuccess: () => setNewName("") });
  }

  async function onDelete(c: CategoryRow) {
    const ok = await confirm({
      title: `Delete category "${c.name}"?`,
      description:
        "Indicators (and KPIs) currently grouped under this category " +
        "will lose the link. This cannot be undone.",
      confirmLabel: "Delete category",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(c.id);
  }

  const items = list.data ?? [];
  const filtered = items;

  const createError = (create.error as
    | { response?: { data?: { name?: string[]; detail?: string } } } | undefined)
    ?.response?.data;
  const createErrorMsg = createError?.name?.[0] || createError?.detail || null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <FolderTree className="h-3.5 w-3.5" />Categories
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Categories</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Shared taxonomy used to group indicators and KPI definitions.
            Codes (C001, …) are auto-assigned.
          </p>
        </div>
      </header>

      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-4">
          <form onSubmit={onCreate} className="flex flex-wrap items-end gap-2">
            <div className="min-w-[14rem] flex-1 space-y-1">
              <Label htmlFor="cat-new">New category</Label>
              <Input
                id="cat-new"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Environmental"
                disabled={create.isPending}
                maxLength={300}
              />
            </div>
            <Button type="submit" disabled={!newName.trim() || create.isPending}>
              {create.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding…</>
                : <><Plus className="mr-2 h-4 w-4" />Add category</>}
            </Button>
          </form>
          {createErrorMsg && (
            <p className="mt-2 text-xs text-destructive">{createErrorMsg}</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FolderTree className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">No categories yet</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Add the first one above. Indicators require at least one.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-3 p-3 hover:bg-muted/30">
                  <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{c.name}</div>
                    {c.code && (
                      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                        {c.code}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {c.code || "—"}
                  </Badge>
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    onClick={() => setEditing(c)} aria-label={`Rename ${c.name}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon"
                    className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    disabled={del.isPending}
                    onClick={() => onDelete(c)} aria-label={`Delete ${c.name}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <EditDialog
        category={editing}
        onClose={() => setEditing(null)}
        onSave={(name) => {
          if (!editing) return;
          update.mutate({ id: editing.id, name }, { onSuccess: () => setEditing(null) });
        }}
        busy={update.isPending}
      />
    </div>
  );
}

function EditDialog({
  category, onClose, onSave, busy,
}: {
  category: CategoryRow | null;
  onClose: () => void;
  onSave: (name: string) => void;
  busy: boolean;
}) {
  const [name, setName] = useState("");
  useEffect(() => { if (category) setName(category.name); }, [category]);

  return (
    <Dialog open={!!category} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename category</DialogTitle>
          <DialogDescription>
            The code stays the same. Indicators referencing this category
            update everywhere automatically.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSave(name.trim()); }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="cat-rename">Name</Label>
            <Input
              id="cat-rename" autoFocus
              value={name} onChange={(e) => setName(e.target.value)}
              disabled={busy}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || busy}>
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />Save</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
